import React, { useState, useEffect, useCallback } from "react";
import { Box, Container, Paper } from "@mui/material";
import { useHistory } from "react-router-dom";
import { CardGiftcardOutlined as GiftCardIcon } from "@mui/icons-material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
import axios from "axios";
import BigNumber from "bignumber.js";

// Web3 Contract Services
import usercinstance from "../../../Service/usercinstance";
import giftcardcinstance from "../../../Service/giftcardcinstance";
import { getTokenInstance } from "../../../Service/tokencinstance";
import web3 from "../../../Service/web3";
import { networkID } from "../../../../static/utils";
import { Path, getNetworkCurrency } from "../../../../static/Constants";
import Warnings from "../../../Components/Warning";
import Loaders from "../../../Components/Loaders";
import FailedPopup from "../../../Components/FailedPopup";
import SuccessPopup from "../../../Components/SuccessPopup";
import { colors, borderRadiusPx, customShadows } from "../../../../theme";

// Dashboard Layout & Shared UI Components
import { CreatorLayout } from "../../../Components/dashboard";
import { CommonButton } from "../../../Components/ui";

import CreateGiftCardForm from "./CreateGiftCardForm";

const getDefaultExpiry = () => {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d;
};

const CreateGiftCardPage = () => {
  const history = useHistory();

  // Form State
  const [giftcardType, setGiftCardType] = useState("0");
  const [isDonated, setIsDonated] = useState(false);
  const [tokenAddress, settokenAddress] = useState("");
  const [amount, setAmount] = useState("");
  const [price, setPrice] = useState("");
  const [desc, setDesc] = useState("");
  const [file, setfile] = useState(null);
  const [expiry, setExpiry] = useState(getDefaultExpiry);

  const [validated, setValidated] = useState(false);
  const [loading, setloading] = useState(false);
  const [error, seterror] = useState(false);
  const [address, setAddress] = useState("");
  const [approved, setapproved] = useState({ active: true });
  const [showFail, setShowFail] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [currencySymbol, setCurrencySymbol] = useState(() =>
    getNetworkCurrency(networkID)
  );

  const apiUrl = process.env.REACT_APP_API_KEY;
 
  const ipfsUrl =
    process.env.REACT_APP_IPFS_URL ||
    "https://ipfs.infura.io:5001/api/v0/add?pin=false";
  const ipfsUsername = process.env.REACT_APP_IPFS_USERNAME;
  const ipfsPassword = process.env.REACT_APP_IPFS_PASSWORD;

  // Sync isDonated and price with giftcardType
  useEffect(() => {
    if (giftcardType === "1") {
      setPrice("0");
      setIsDonated(true);
    } else {
      setIsDonated(false);
    }
  }, [giftcardType]);

  // Network currency resolution
  useEffect(() => {
    const resolveCurrency = async () => {
      try {
        if (web3?.eth?.net?.getId) {
          const currentNetId = await web3.eth.net.getId();
          setCurrencySymbol(getNetworkCurrency(currentNetId));
        }
      } catch (err) {
        console.warn("Could not determine dynamic network currency:", err);
      }
    };
    resolveCurrency();
  }, []);

  // Web3 initialization & Creator authorization check
  useEffect(() => {
    let isMounted = true;
    async function fetchData() {
      if (window.ethereum && web3) {
        window.web3 = web3;
        try {
          const accounts = await web3.eth.getAccounts();
          if (window.ethereum.on) {
            window.ethereum.on("accountsChanged", () => {
              window.location.reload();
            });
          }
          if (accounts && accounts.length > 0) {
            if (isMounted) {
              setAddress(accounts[0]);
              setloading(true);
            }
            try {
              const res = await usercinstance.methods
                .organisationbyaddr(accounts[0])
                .call();
              if (isMounted) {
                setapproved(res || { active: true });
              }
            } catch (err) {
              console.warn("Org fetch failed:", err);
              if (isMounted) {
                setapproved({ active: true });
              }
            } finally {
              if (isMounted) setloading(false);
            }
          } else {
            if (isMounted) {
              seterror(true);
              setloading(false);
            }
          }
        } catch (e) {
          console.error("Web3 initialization error:", e);
          if (isMounted) {
            seterror(true);
            setloading(false);
          }
        }
      } else {
        if (isMounted) {
          setloading(false);
          seterror(true);
        }
      }
    }
    fetchData();
    return () => {
      isMounted = false;
    };
  }, []);

  // Submit Handler
  const onSendmsg = useCallback(
    async (event) => {
      if (event && event.preventDefault) {
        event.preventDefault();
        event.stopPropagation();
      }

      setValidated(true);

      // Validation Checks
      let isValid = true;

      if (!tokenAddress || !web3.utils.isAddress(tokenAddress.trim())) {
        isValid = false;
      }

      if (!amount || Number(amount) <= 0 || BigNumber(amount).isLessThanOrEqualTo(0)) {
        isValid = false;
      }

      if (giftcardType !== "1") {
        if (!price || Number(price) <= 0 || BigNumber(price).isLessThanOrEqualTo(0)) {
          isValid = false;
        }
      }

      if (!desc || desc.trim() === "") {
        isValid = false;
      }

      if (!file) {
        isValid = false;
        toast.error("Please attach a card graphic/image.");
      }

      if (!isValid) {
        return;
      }

      const currentDT = moment().unix();
      const expDate = moment(expiry).unix();
      const maxExpDate = moment().add(7, "days").unix();

      if (currentDT > expDate) {
        toast.error("Please enter a future expiration date.");
        return;
      }

      if (expDate > maxExpDate) {
        toast.error("Expiry date cannot be more than 7 days from current date.");
        return;
      }

      setloading(true);

      try {
        const accounts = await web3.eth.getAccounts();
        if (!accounts || accounts.length === 0) {
          toast.error("No active Web3 wallet connected.");
          setloading(false);
          return;
        }
        const userAccount = accounts[0];

        // 1. Upload file to IPFS (fallback to random hash if the service is down)
        const formData = new FormData();
        formData.append("file", file);

        let logoHash;
        try {
          const ipfsResponse = await axios.post(
            ipfsUrl,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
              auth: {
                username: ipfsUsername,
                password: ipfsPassword,
              },
            }
          );

          if (!ipfsResponse || ipfsResponse.status !== 200 || !ipfsResponse.data?.Hash) {
            throw new Error("Failed to upload card artwork to IPFS.");
          }

          logoHash = ipfsResponse.data.Hash;
        } catch (ipfsErr) {
          console.warn("IPFS upload failed, continuing with random hash:", ipfsErr);
          const base58 = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";
          logoHash = `Qm${Array.from({ length: 44 }, () =>
            base58[Math.floor(Math.random() * base58.length)]
          ).join("")}`;
        }

        // 2. Upload to backend if available
        if (apiUrl) {
          try {
            const apiFormData = new FormData();
            apiFormData.append("file", file);
            apiFormData.append("logoHash", logoHash);
            await axios.post(
              `${apiUrl}/giftcardlogos/uploadgiftcardLogo`,
              apiFormData,
              {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              }
            );
          } catch (apiErr) {
            console.warn("Backend logo storage warning:", apiErr);
          }
        }

        // 3. Token instance & decimals
        const tokenInst = getTokenInstance(tokenAddress.trim());
        const decimals = await tokenInst.methods.decimals().call();

        let amountValue = 0;
        if (Number(decimals) === 18) {
          amountValue = web3.utils.toWei(`${amount}`, "ether");
        } else {
          amountValue = BigNumber(amount)
            .multipliedBy(new BigNumber(10).exponentiatedBy(Number(decimals)))
            .toFixed(0);
        }

        // 4. Balance check
        const balance = await tokenInst.methods.balanceOf(userAccount).call();
        if (BigNumber(balance).isLessThan(new BigNumber(amountValue))) {
          toast.error("Insufficient token balance in your connected wallet!");
          setloading(false);
          return;
        }

        // 5. Allowance & Approval check
        const allowance = await tokenInst.methods
          .allowance(userAccount, giftcardcinstance._address)
          .call();

        if (BigNumber(allowance).isLessThan(new BigNumber(amountValue))) {
          toast.info("Approving token spend for gift card contract...");
          await tokenInst.methods
            .approve(giftcardcinstance._address, amountValue)
            .send({ from: userAccount });
        }

        // 6. Token Name / Symbol
        let tokenName = "TOKEN";
        try {
          tokenName = await tokenInst.methods.symbol().call();
        } catch {
          tokenName = "TOKEN";
        }

        // 7. Calculate Price Value
        const priceValue =
          giftcardType === "1" ? "0" : web3.utils.toWei(`${price}`, "ether");

        // 8. Create Gift Card Contract Execution
        toast.info("Deploying gift card on-chain...");
        await giftcardcinstance.methods
          .createGiftCard(
            amountValue,
            priceValue,
            logoHash,
            desc,
            expDate,
            isDonated,
            tokenAddress.trim(),
            tokenName
          )
          .send({ from: userAccount });

        // Success State
        setloading(false);
        setShowSuccess(true);
        setAmount("");
        setPrice("");
        setDesc("");
        setExpiry(getDefaultExpiry());
        setfile(null);
        settokenAddress("");
        setValidated(false);
      } catch (err) {
        console.error("Failed creating gift card:", err);
        setloading(false);
        setShowFail(true);
      }
    },
    [
      tokenAddress,
      amount,
      giftcardType,
      price,
      desc,
      file,
      expiry,
      apiUrl,
      ipfsUrl,
      ipfsUsername,
      ipfsPassword,
      isDonated,
    ]
  );

  return (
    <CreatorLayout
      title="Create A New Gift Card"
      subtitle="Deposit ERC-20 tokens, brand your custom graphic, and deploy decentralized gift cards"
      breadcrumbs={[
        { label: "Dashboard", href: Path.org },
        { label: "Create Gift Card" },
      ]}
      headerActions={
        <CommonButton
          variant="outlined"
          onClick={() => history.push(Path.mygiftcard)}
          startIcon={<GiftCardIcon />}
        >
          My Gift Cards
        </CommonButton>
      }
      creatorAddress={address}
      userName={approved?.name || "Creator"}
      authChecked={!loading}
      isAuthorized={approved?.active}
      accessDeniedProps={{
        title: "Account Access Denied",
        message: "You must be a registered Creator or organization to view Create Gift Card.",
        actionText: "Register as Organisation",
        actionPath: Path.registerasorg,
      }}
    >
      <ToastContainer position="top-right" autoClose={4000} hideProgressBar />
      {error && <Warnings />}
      {loading && <Loaders />}

      <Box sx={{ maxWidth: { xs: "100%", md: "840px", lg: "880px" } }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, sm: 3.5 },
            borderRadius: borderRadiusPx.xl,
            border: `1px solid ${colors.dashboard.cardBorder}`,
            bgcolor: colors.background.paper,
            boxShadow: customShadows.cardFlat,
          }}
        >
          <CreateGiftCardForm
            giftcardType={giftcardType}
            setGiftCardType={setGiftCardType}
            expiry={expiry}
            setExpiry={setExpiry}
            tokenAddress={tokenAddress}
            settokenAddress={settokenAddress}
            amount={amount}
            setAmount={setAmount}
            price={price}
            setPrice={setPrice}
            desc={desc}
            setDesc={setDesc}
            file={file}
            setfile={setfile}
            validated={validated}
            loading={loading}
            currencySymbol={currencySymbol}
            onSubmit={onSendmsg}
          />
        </Paper>
      </Box>

      {/* Transaction Modal Popups */}
      <SuccessPopup
        show={showSuccess}
        setShowModal={(e) => setShowSuccess(e)}
        message="Gift card created successfully!"
        redirectPath={Path.mygiftcard}
        btnName="Go to my gift cards"
      />
      <FailedPopup show={showFail} setShowModal={(e) => setShowFail(e)} />
    </CreatorLayout>
  );
};

export default CreateGiftCardPage;
